import { h, dialog, errorText } from '../components/ui.js';
import { buildPlan, runImport, verifyTotals, readLocalLegacy } from '../migrations/legacyImport.js';
import { sha256 } from '../services/storageService.js';

const LABELS = { products: 'Produtos', product_variations: 'Variações', filaments: 'Filamentos', stock_items: 'Insumos, embalagens e outros', sales: 'Vendas', productions: 'Produções', purchases: 'Compras', stock_movements: 'Movimentações', media_assets: 'Fotografias' };
const download = (name, text) => { const a = h('a', { href: URL.createObjectURL(new Blob([text], { type: 'application/json' })), download: name }); a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };

function table(counts, actual) {
  return h('table', { class: 'pc-table' }, h('thead', {}, h('tr', {}, h('th', {}, 'Dados'), h('th', {}, 'Na origem'), actual ? h('th', {}, 'No servidor') : null)),
    h('tbody', {}, Object.entries(LABELS).map(([k, l]) => h('tr', {}, h('td', {}, l), h('td', {}, counts[k] ?? 0), actual ? h('td', {}, actual[k] ?? 0) : null))));
}
function list(title, items, cls) {
  if (!items.length) return null;
  return h('details', { class: cls, open: cls === 'pc-errors' }, h('summary', {}, `${title} (${items.length})`), h('ul', {}, items.slice(0, 200).map(i => h('li', {}, i))));
}

export function openWizard({ ws, api, mode = 'merge', file = null, onDone }) {
  const restore = mode === 'replace';
  const area = h('div', { class: 'pc-body' });
  const { close } = dialog(restore ? 'Restaurar backup' : 'Migrar dados do Print Manager', area);
  const set = (...n) => area.replaceChildren(...n.filter(Boolean));

  async function analyze(text, source) {
    set(h('p', {}, 'Analisando dados...'));
    try {
      const raw = JSON.parse(text);
      const plan = buildPlan(raw);
      const hash = await sha256(new Blob([text]));
      review(plan, hash, source, text);
    } catch (e) { set(h('p', { class: 'pc-msg err' }, 'Arquivo recusado: ' + errorText(e) + ' Nada foi alterado.'), h('button', { class: 'btn btn-secondary', onclick: start }, 'Voltar')); }
  }
  function review(plan, hash, source, text) {
    const confirmBox = h('input', { type: 'checkbox', id: 'pcOk' });
    const go = h('button', { class: 'btn btn-primary', disabled: true, onclick: () => execute(plan, hash) }, restore ? 'Restaurar agora' : 'Iniciar migração');
    confirmBox.addEventListener('change', () => { go.disabled = !confirmBox.checked; });
    set(h('p', {}, `Origem: ${source}`), table(plan.counts),
      list('Erros que impedem registros', plan.errors, 'pc-errors'), list('Avisos', plan.warnings, 'pc-warn'),
      h('p', { class: 'pc-note' }, restore
        ? 'Os dados atuais serão substituídos pelos do backup. Registros ausentes são marcados como excluídos, não destruídos. Baixe um backup atual antes de continuar.'
        : 'A origem não é alterada. Seu arquivo e os dados deste navegador permanecem intactos.'),
      h('div', { class: 'pc-row' },
        h('button', { class: 'btn btn-secondary', onclick: () => download(`print-manager-copia-pre-migracao-${new Date().toISOString().slice(0, 10)}.json`, text) }, 'Baixar cópia de segurança')),
      h('label', { class: 'pc-check', for: 'pcOk' }, confirmBox, h('span', {}, restore ? 'Entendo que os dados atuais serão substituídos.' : 'Conferi as quantidades acima.')),
      go);
  }
  async function execute(plan, hash) {
    const bar = h('progress', { max: 100, value: 0 }), label = h('p', {}, 'Iniciando...');
    set(label, bar);
    try {
      const report = await runImport({ plan, ws, hash, mode, api, onProgress: p => { bar.value = Math.round(p.done / p.total * 100); label.textContent = `${p.label} (${p.done}/${p.total})`; } });
      const loaded = await api.reload();
      plan.__mode = mode === 'merge' && api.hadData ? 'merge' : 'exact';
      const check = verifyTotals(plan, loaded.entries);
      const ok = report.status === 'validated' && check.ok;
      set(h('p', { class: 'pc-msg ' + (ok ? 'ok' : 'err') }, ok
        ? 'Migração validada: contagens e totais conferem com a origem.'
        : 'A migração não foi validada. Os dados de origem seguem intactos; corrija e repita com o mesmo arquivo, sem risco de duplicar.'),
        table(report.expected, report.actual),
        check.ok ? null : h('p', { class: 'pc-note' }, `Totais divergentes: ${check.diffs.join(', ')}`),
        list('Erros', report.errors, 'pc-errors'), list('Avisos', report.warnings, 'pc-warn'),
        h('button', { class: 'btn btn-primary', onclick: () => { close(); onDone?.(); } }, 'Concluir'));
    } catch (e) {
      set(h('p', { class: 'pc-msg err' }, errorText(e)),
        h('p', { class: 'pc-note' }, 'Importações podem ser retomadas: use o mesmo arquivo. O que já entrou não é duplicado.'),
        h('button', { class: 'btn btn-secondary', onclick: start }, 'Voltar'));
    }
  }
  async function start() {
    const local = restore ? null : await readLocalLegacy().catch(() => null);
    const pick = h('input', { type: 'file', accept: '.json,application/json', onchange: e => { const f = e.target.files[0]; if (f) f.text().then(t => analyze(t, f.name)); } });
    set(h('p', {}, restore ? 'Selecione um backup JSON.' : 'No Print Manager antigo, abra Configurações e clique em Backup JSON. Depois selecione o arquivo aqui.'),
      h('p', { class: 'pc-note' }, 'Navegadores isolam os dados por endereço: este site só enxerga dados locais se o sistema antigo rodava neste mesmo endereço. Por isso o arquivo de backup é o caminho recomendado.'),
      pick, local ? h('button', { class: 'btn btn-secondary', onclick: () => analyze(JSON.stringify(local), 'dados locais deste navegador') }, 'Usar dados encontrados neste navegador') : null);
  }
  if (file) file.text().then(t => analyze(t, file.name)); else start();
}
